// import { concat, isNull } from "lodash";
import React, { createContext, useContext, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import validator from "validator";

// Imported actions
import { userLogin as login, userLogout as logout } from "../actions/auth.js";
import { updatePassword, updateUser } from "../actions/user.js";

// Imported utils
import { isLoggedIn, getUser } from "../utils/user.js";
import { getFromLocalStorage } from "../utils/storage.js";

// Constants
const emailMax = 320, // Maximum email length
  // passwordMax = 64, // Maximum password length
  passwordMin = 12; // Minimum password length

const initialContext = {
  isAuthenticated: false,
  user: null,
  emailMax: emailMax,
  passwordMin: passwordMin,
  // passwordMax: passwordMax,
  // userLogin: null,
  // registerUser: registerUser,
  // changePassword: null,
  // userLogout: null,
  // validateInputs: null,
  // updateUserData: null,
};

interface LoginInputErrors {
  emailErr: string | null;
  passwordErr: string | null;
}

interface ChangePasswordInputErrors {
  oldPasswordErr: string | null;
  newPasswordErrs: NewPasswordErrors | null;
  oldAndNewMatchErr: string | null;
  newAndConfirmMatchErr: string | null;
}

export interface ResetPasswordInputErrors {
  newPasswordErrs: NewPasswordErrors | null;
  newAndConfirmMatchErr: string | null;
}

interface NewPasswordErrors {
  lengthErr: boolean;
  lowerErr: boolean;
  upperErr: boolean;
  numErr: boolean;
  specialErr: boolean;
  recentErr: boolean;
}

interface UserContextType {
  isAuthenticated: boolean;
  user: any;
  emailMax: number;
  passwordMin: number;
  userLogin: (email: string, password: string) => Promise<any>;
  userLogout: () => Promise<any>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<any>;
  validateInputs: (mode: string, inputs: any) => any;
  updateUserData: (userData: any) => Promise<any>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);
export const UserProvider = ({ children }) => {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(isLoggedIn());
  const [user, setUser] = useState(getUser());

  const userLogin = async (email, password) => {
    return new Promise(async (resolve, reject) => {
      await login(email, password)
        .then(async (resp) => {
          setUser(getUser());
          setAuthenticated(true);
          resolve(resp.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // const registerUser = async (email, password) => {
  //   return new Promise(async (resolve, reject) => {
  //     await register(email, password)
  //       .then(async (resp) => {
  //         setUser(getUser());
  //         setAuthenticated(true);
  //         setNotifications(await getNotifications(resp.id));
  //         resolve(resp);
  //       })
  //       .catch((err) => {
  //         reject(err);
  //       });
  //   });
  // };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    const id = getFromLocalStorage("authData").id; // user's id

    return new Promise(async (resolve, reject) => {
      await updatePassword({ id, currentPassword, newPassword })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const userLogout = async () => {
    return new Promise(async (resolve, reject) => {
      await logout()
        .then((resp) => {
          setUser(null);
          setAuthenticated(false);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const validateEmail = (email) => {
    let err: string | null = null;

    if (email) {
      if (validator.isEmail("" + email)) {
        if (email.length > emailMax) err = "Email is too long.";
      } else err = "Email is invalid.";
    } else err = "Email is required.";

    return err;
  };

  const validatePassword = (password) => {
    let err: string | null = null;

    if (password) {
      // if (password.length < passwordMin) err = "Password is too short.";
      // else if (password.length > passwordMax) err = "Password is too long.";
    } else err = "Password is required.";

    return err;
  };

  const validateNewPassword = (password) => {
    let errs: NewPasswordErrors = {
      lengthErr: true,
      lowerErr: true,
      upperErr: true,
      numErr: true,
      specialErr: true,
      recentErr: false,
    };

    // TODO: Add check for password recency? would require communication with backend
    // Should only check upon attempting to save changes however

    if (password) {
      if (password.length >= passwordMin) errs.lengthErr = false;
      // if (password.length <= passwordMax) errs.lengthErr = false;
      if (password.search(/.*[a-z].*/) > -1) errs.lowerErr = false;
      if (password.search(/.*[A-Z].*/) > -1) errs.upperErr = false;
      if (password.search(/.*[0-9].*/) > -1) errs.numErr = false;
      // Special character regex obtained from: https://stackoverflow.com/a/74095934
      if (password.search(/.*[!-\/:-@[-`{-~].*/) > -1) errs.specialErr = false;
    }

    return errs;
  };

  const validateInputs = (mode: string, inputs) => {
    switch (mode) {
      case "Login": {
        let errs: LoginInputErrors = {
          emailErr: null,
          passwordErr: null,
        };

        errs.emailErr = validateEmail(inputs.email);
        errs.passwordErr = validatePassword(inputs.password);

        return errs;
      }

      // case "Register":
      // if (mode === "Register" && !(inputs.password === inputs.confirmPassword)) {
      //   errs.confirmPassword = "Password and confirm password must match.";
      // } else if (mode === "Register") errs.confirmPasswordErr = null;

      case "ChangePassword": {
        let errs: ChangePasswordInputErrors = {
          oldPasswordErr: null,
          newPasswordErrs: null,
          oldAndNewMatchErr: null,
          newAndConfirmMatchErr: null,
        };

        errs.oldPasswordErr = validatePassword(inputs.oldPassword);
        errs.newPasswordErrs = validateNewPassword(inputs.newPassword);

        if (inputs.oldPassword === inputs.newPassword) {
          errs.oldAndNewMatchErr =
            "Current password and new password must not match.";
        } else errs.oldAndNewMatchErr = null;

        if (!(inputs.newPassword === inputs.confirmPassword)) {
          errs.newAndConfirmMatchErr =
            "New password and confirm password must match.";
        } else errs.newAndConfirmMatchErr = null;

        return errs;
      }

      case "ResetPassword": {
        let errs: ResetPasswordInputErrors = {
          newPasswordErrs: null,
          newAndConfirmMatchErr: null,
        };

        errs.newPasswordErrs = validateNewPassword(inputs.newPassword);

        if (!(inputs.newPassword === inputs.confirmPassword)) {
          errs.newAndConfirmMatchErr =
            "New password and confirm password must match.";
        } else errs.newAndConfirmMatchErr = null;
        
        return errs;
      }

      default: {
        console.error(`Error: Unknown Mode '${mode}' when validating inputs`);
        break;
      }
    }
  };

  const updateUserData = async (userData) => {
    return new Promise(async (resolve, reject) => {
      await updateUser(userData)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const value = {
    isAuthenticated: isAuthenticated,
    user: user,
    emailMax: emailMax,
    passwordMin: passwordMin,
    // passwordMax: passwordMax,
    userLogin: userLogin,
    // registerUser: registerUser,
    changePassword: changePassword,
    userLogout: userLogout,
    validateInputs: validateInputs,
    updateUserData: updateUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const RequireAuth = (/*props*/) => {
  let { isAuthenticated } = useContext(UserContext);
  let location = useLocation();

  return isAuthenticated ? (
    <Outlet />
  ) : (
    <Navigate to="login" state={{ path: location.pathname }} replace />
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};